import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";

// ─── Shared color palette (matches BalleranoSite-v2) ───
const C = {
    bg: "#F3F3F5",
    bgCard: "#FFFFFF",
    bgDark: "#1A1A2E",
    text: "#1A1A2E",
    textMuted: "#5A5A72",
    textLight: "#68687F",
    accent: "#2563EB",
    accentDark: "#1D4ED8",
    border: "rgba(26,26,46,0.08)",
};

export default function SubPageLayout({ children }: { children: React.ReactNode }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    const navLinks: Array<{ label: string; href: string; external?: boolean }> = [
        { label: "Chi Sono", href: "/chi-sono" },
        { label: "IA Upgrade", href: "https://www.hwupgrade.it/tag/IA-Upgrade/", external: true },
        { label: "Substack", href: "https://lucianoballerano.substack.com", external: true },
        { label: "Vibe Coder", href: "/vibe-coder" },
        { label: "Newsletter", href: "/newsletter" },
        { label: "Blog", href: "/blog" },
        { label: "Extra", href: "/extra" },
        { label: "Servizi", href: "/servizi" },
        { label: "Metodo", href: "/metodologia" },
    ];

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.text, background: C.bg, minHeight: "100vh" }}>
            <style>{`
                * { margin: 0; padding: 0; box-sizing: border-box; }
                html { scroll-behavior: smooth; }
                ::selection { background: ${C.accent}; color: white; }
                .nav-link-sub { position: relative; color: rgba(255,255,255,0.78); text-decoration: none; font-size: 13px; font-weight: 500; letter-spacing: 0.015em; white-space: nowrap; }
                .nav-link-sub::after { content:""; position:absolute; bottom:-2px; left:0; width:0; height:2px; background:white; transition:width 0.3s ease; }
                .nav-link-sub:hover::after { width: 100%; }
                .nav-link-sub:hover { color: white; }
                .desktop-nav { display: flex; }
                .mobile-toggle { display: none; }
                .card-hover { transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
                .card-hover:hover { transform: translateY(-4px); box-shadow: 0 8px 40px rgba(37,99,235,0.10), 0 2px 8px rgba(26,26,46,0.06); }
                @media (max-width: 768px) {
                    .desktop-nav { display: none !important; }
                    .mobile-toggle { display: block !important; }
                    .grid-2 { grid-template-columns: 1fr !important; gap: 28px !important; }
                    .grid-3 { grid-template-columns: 1fr !important; gap: 16px !important; }
                    .grid-4 { grid-template-columns: 1fr !important; gap: 16px !important; }
                    .stats-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
                    .card-hover { padding: 24px !important; }
                    section { padding-left: 16px !important; padding-right: 16px !important; }
                    main { padding-top: 70px !important; }
                    .mobile-sticky-cta { display: block !important; }
                    footer { padding-bottom: 80px !important; }
                }
                @media (max-width: 480px) {
                    .stats-grid { grid-template-columns: 1fr !important; }
                    main { padding-top: 64px !important; }
                    footer { padding-bottom: 80px !important; }
                }
            `}</style>


            {/* NAVIGATION (dark bar, same as home) */}
            <nav style={{
                position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
                padding: scrolled ? "10px 0" : "16px 0",
                background: scrolled ? "rgba(26,26,46,0.95)" : "rgba(26,26,46,0.85)",
                backdropFilter: "blur(20px)",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                transition: "all 0.4s ease",
            }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Link to="/" style={{ textDecoration: "none", color: "white", display: "flex", alignItems: "center", gap: 10 }}>
                        <img src="/logo-lb-sq.png" alt="LB" loading="eager" decoding="async" width={34} height={34} style={{ width: 34, height: 34, borderRadius: 9, objectFit: "contain" }} />
                        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, letterSpacing: "-0.02em" }}>
                            Luciano Ballerano
                        </span>
                    </Link>
                    <div style={{ display: "flex", gap: 22, alignItems: "center" }} className="desktop-nav">
                        {navLinks.map(link => link.external ? (
                            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="nav-link-sub">{link.label}</a>
                        ) : (
                            <Link key={link.href} to={link.href} className="nav-link-sub">{link.label}</Link>
                        ))}
                        <Link to="/umani-per-ora"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px",
                                background: C.accent, color: "white", textDecoration: "none", borderRadius: 8,
                                fontWeight: 600, fontSize: 13, boxShadow: "0 2px 12px rgba(37,99,235,0.25)" }}>
                            <BookOpen size={14} /> Il libro
                        </Link>
                    </div>
                    <button onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu" className="mobile-toggle"
                        style={{ display: "none", background: "none", border: "none", color: "white", cursor: "pointer", padding: 8 }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {mobileMenu ? <path d="M6 6l12 12M6 18L18 6" /> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
                        </svg>
                    </button>
                </div>
                {mobileMenu && (
                    <div style={{ padding: "16px 24px", background: "rgba(26,26,46,0.97)", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                        {navLinks.map(link => {
                            const style = { display: "block", padding: "12px 0", color: "rgba(255,255,255,0.8)", textDecoration: "none", fontSize: 16, borderBottom: "1px solid rgba(255,255,255,0.06)" };
                            return link.external ? (
                                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenu(false)} style={style}>
                                    {link.label}
                                </a>
                            ) : (
                                <Link key={link.href} to={link.href} onClick={() => setMobileMenu(false)} style={style}>
                                    {link.label}
                                </Link>
                            );
                        })}
                        <Link to="/umani-per-ora" onClick={() => setMobileMenu(false)}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 16, padding: "14px 0",
                                background: C.accent, color: "white", textDecoration: "none", borderRadius: 10, fontWeight: 600, fontSize: 15 }}>
                            <BookOpen size={16} /> Il libro
                        </Link>
                    </div>
                )}
            </nav>


            {/* CONTENT */}
            <main style={{ paddingTop: 80 }}>
                {children}
            </main>

            {/* STICKY MOBILE CTA */}
            <div className="mobile-sticky-cta" style={{
                display: "none", position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99,
                padding: "12px 16px", background: "rgba(26,26,46,0.97)",
                backdropFilter: "blur(16px)", borderTop: "1px solid rgba(255,255,255,0.1)",
            }}>
                <Link to="/umani-per-ora"
                    style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                        color: "white", textDecoration: "none", borderRadius: 12, fontWeight: 700,
                        fontSize: 15, boxShadow: "0 4px 24px rgba(59,130,246,0.35)",
                    }}>
                    <BookOpen size={16} /> Scopri il libro
                </Link>
            </div>

            {/* FOOTER (dark, same as home) */}
            <footer style={{ padding: "36px 24px", background: "#0D0D1A", color: "white", textAlign: "center" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 14 }}>
                        <img src="/logo-lb-sq.png" alt="LB" loading="lazy" decoding="async" width={26} height={26} style={{ width: 26, height: 26, borderRadius: 7 }} />
                        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: "rgba(255,255,255,0.8)" }}>Luciano Ballerano</span>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>· Scrivo di AI e di persone</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
                        <Link to="/privacy-policy" style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>Privacy Policy</Link>
                        <Link to="/cookie-policy" style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>Cookie Policy</Link>
                        <Link to="/termini-servizio" style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>Termini e Condizioni</Link>
                    </div>
                    <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{`© ${new Date().getFullYear()} Luciano Ballerano. Tutti i diritti riservati.`}</p>
                </div>
            </footer>
        </div>
    );
}
